// TODO [BUG-039]: Migrate from google.maps.places.Autocomplete (deprecated) to the new
// Places API (google.maps.places.PlaceAutocompleteElement / Place class).
// See: https://developers.google.com/maps/documentation/javascript/place-autocomplete-new
// The legacy Autocomplete widget still works but will be removed in a future Maps JS SDK version.
import { useEffect, useRef, useState, useCallback } from 'react';

export interface ParsedAddress {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  latitude: number | null;
  longitude: number | null;
  formattedAddress: string;
}

interface AddressAutocompleteProps {
  onAddressSelect: (address: ParsedAddress) => void;
  /** Google Maps API key. Required for autocomplete to work. */
  apiKey?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  id?: string;
  /**
   * Google Places `types` filter. Defaults to `['address']` (precise street
   * addresses only). Pass `[]` to search every place type — street addresses
   * PLUS establishments/premises (a salon inside a mall, a new-build Google
   * only knows as a business). Read once, when the widget initialises.
   */
  types?: string[];
}

const DEFAULT_TYPES = ['address'];

let googleMapsLoaded = false;
let googleMapsLoading = false;
const loadCallbacks: (() => void)[] = [];

/** How long we wait for the Maps script before unlocking manual entry. */
const MAPS_LOAD_TIMEOUT_MS = 8000;

// window.gm_authFailure is Google's only signal for "key rejected / referrer
// not allowed". It fires once, globally, so fan it out to every mounted input
// (and remember it for inputs mounted afterwards).
let mapsAuthFailed = false;
const authFailureListeners = new Set<() => void>();
let authFailureHookInstalled = false;

function subscribeAuthFailure(listener: () => void): () => void {
  if (mapsAuthFailed) listener();
  authFailureListeners.add(listener);
  if (!authFailureHookInstalled && typeof window !== 'undefined') {
    authFailureHookInstalled = true;
    const w = window as unknown as { gm_authFailure?: () => void };
    const previous = w.gm_authFailure;
    w.gm_authFailure = () => {
      mapsAuthFailed = true;
      console.error('Google Maps rejected the API key or referrer — falling back to manual address entry');
      authFailureListeners.forEach((cb) => cb());
      previous?.();
    };
  }
  return () => {
    authFailureListeners.delete(listener);
  };
}

function loadGoogleMapsScript(apiKey: string): Promise<void> {
  if (googleMapsLoaded) return Promise.resolve();

  return new Promise((resolve) => {
    if (googleMapsLoading) {
      loadCallbacks.push(resolve);
      return;
    }

    if (!apiKey) {
      console.warn('Google Maps API key not provided');
      resolve();
      return;
    }

    googleMapsLoading = true;

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      googleMapsLoaded = true;
      googleMapsLoading = false;
      resolve();
      loadCallbacks.forEach((cb) => cb());
      loadCallbacks.length = 0;
    };
    script.onerror = () => {
      googleMapsLoading = false;
      console.error('Failed to load Google Maps script');
      resolve();
    };
    document.head.appendChild(script);
  });
}

export function parseGooglePlace(place: google.maps.places.PlaceResult): ParsedAddress {
  const result: ParsedAddress = {
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    latitude: null,
    longitude: null,
    formattedAddress: place.formatted_address || '',
  };

  if (place.geometry?.location) {
    result.latitude = place.geometry.location.lat();
    result.longitude = place.geometry.location.lng();
  }

  let streetNumber = '';
  let route = '';
  // City fallbacks, in priority order. Google returns the city under
  // different component types by locale: `locality` for most, `postal_town`
  // in the UK, but Brazilian and some other addresses put it only in
  // `administrative_area_level_2` or a `sublocality`. Without these fallbacks
  // `city` stays empty and the onboarding validation ("Select your address
  // from the suggestions") blocks a user who DID select a valid suggestion.
  let cityLocality = '';
  let cityAdmin2 = '';
  let citySublocality = '';

  for (const component of place.address_components || []) {
    const types = component.types;

    if (types.includes('street_number')) {
      streetNumber = component.long_name;
    } else if (types.includes('route')) {
      route = component.long_name;
    } else if (types.includes('locality') || types.includes('postal_town')) {
      cityLocality = component.long_name;
    } else if (types.includes('administrative_area_level_2')) {
      cityAdmin2 = component.long_name;
    } else if (
      types.includes('sublocality_level_1') ||
      types.includes('sublocality')
    ) {
      citySublocality = component.long_name;
    } else if (types.includes('administrative_area_level_1')) {
      result.state = component.long_name;
    } else if (types.includes('postal_code')) {
      result.postalCode = component.long_name;
    } else if (types.includes('country')) {
      result.country = component.long_name;
    }
  }

  result.city = cityLocality || cityAdmin2 || citySublocality;

  if (route && streetNumber) {
    result.street = `${route} ${streetNumber}`;
  } else if (route) {
    result.street = route;
  } else if (streetNumber) {
    result.street = streetNumber;
  }

  return result;
}

/**
 * Google Places address autocomplete input.
 *
 * Uses an uncontrolled input so Google Places can manage the value
 * directly via the DOM — controlled inputs conflict with the Places
 * Autocomplete widget which sets the input value on selection.
 */
export function AddressAutocomplete({
  onAddressSelect,
  apiKey = '',
  placeholder = 'Start typing an address...',
  className,
  disabled,
  id,
  types = DEFAULT_TYPES,
}: AddressAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [isLoaded, setIsLoaded] = useState(googleMapsLoaded);
  // Manual-entry fallback. The input must NEVER stay disabled forever: with
  // no API key (2026-08 incident — the booking bundle shipped an empty key
  // and every MOBILE customer was stuck on a dead "Loading address search..."
  // field), a script that fails/never loads (blocked network, ad-blocker) or
  // a Google auth failure (bad key / referrer), we degrade to a plain text
  // input and report the typed text as `formattedAddress`. Structured fields
  // stay empty so callers that need geocoded parts can tell it was manual.
  const [manual, setManual] = useState(!apiKey && !googleMapsLoaded);
  const onAddressSelectRef = useRef(onAddressSelect);
  onAddressSelectRef.current = onAddressSelect;
  const typesRef = useRef(types);

  const initAutocomplete = useCallback(() => {
    if (!inputRef.current || !window.google?.maps?.places || autocompleteRef.current) return;

    const autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
      ...(typesRef.current.length ? { types: typesRef.current } : {}),
      fields: ['address_components', 'formatted_address', 'geometry'],
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place.address_components) return;

      const parsed = parseGooglePlace(place);
      onAddressSelectRef.current(parsed);
    });

    autocompleteRef.current = autocomplete;
  }, []);

  useEffect(() => {
    if (!apiKey) {
      setManual(true);
      return;
    }
    if (googleMapsLoaded) {
      setIsLoaded(true);
      return;
    }
    let cancelled = false;
    // If Google hasn't answered within the timeout, unlock manual entry. A
    // late successful load still upgrades the field to autocomplete below.
    const timer = window.setTimeout(() => {
      if (!cancelled && !googleMapsLoaded) setManual(true);
    }, MAPS_LOAD_TIMEOUT_MS);
    loadGoogleMapsScript(apiKey).then(() => {
      if (cancelled) return;
      window.clearTimeout(timer);
      // loadGoogleMapsScript resolves on error too — only trust the flag.
      if (googleMapsLoaded && window.google?.maps?.places) {
        setManual(false);
        setIsLoaded(true);
      } else {
        setManual(true);
      }
    });
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [apiKey]);

  useEffect(() => {
    if (isLoaded) {
      initAutocomplete();
    }
  }, [isLoaded, initAutocomplete]);

  // Google reports a rejected key / disallowed referrer asynchronously via
  // window.gm_authFailure (and disables the Places-bound input itself), so
  // treat it as a load failure and fall back to manual entry.
  useEffect(() => {
    return subscribeAuthFailure(() => setManual(true));
  }, []);

  useEffect(() => {
    return () => {
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, []);

  const ready = isLoaded || manual;

  return (
    <input
      ref={inputRef}
      id={id}
      type="text"
      placeholder={ready ? placeholder : 'Loading address search...'}
      className={className}
      disabled={disabled || !ready}
      autoComplete={manual ? 'street-address' : 'off'}
      onChange={
        manual
          ? (e) =>
              onAddressSelectRef.current({
                street: '',
                city: '',
                state: '',
                postalCode: '',
                country: '',
                latitude: null,
                longitude: null,
                formattedAddress: e.target.value,
              })
          : undefined
      }
    />
  );
}
